import { Config } from '../config';

// Role type definition - should match AuthContext.UserRole
export type UserRole = 'admin' | 'employee' | 'super_admin' | 'sales' | 'project_manager';

export interface User {
  uid: string;
  email: string | null;
  displayName: string | null;
  roles: UserRole[];
  employeeId?: string;
  companyId?: string;
  firstName?: string;
  lastName?: string;
}

export interface RegisterData {
  email: string;
  password: string;
  displayName: string;
  roles?: UserRole[];
  employeeId?: string;
  companyId?: string;
  firstName?: string;
  lastName?: string;
}

class AuthService {
  private baseUrl: string;

  constructor() {
    this.baseUrl = `${Config.Env.ApiBaseUrl}/auth`;
  }

  /**
   * Get Firebase ID token for authenticated requests
   */
  private async getAuthToken(): Promise<string | null> {
    const firebase = await import('firebase');
    const currentUser = firebase.default.auth().currentUser;
    
    if (!currentUser) {
      return null;
    }
    
    return await currentUser.getIdToken();
  }

  /**
   * Make authenticated API request
   */
  private async authenticatedRequest(url: string, options: RequestInit = {}): Promise<Response> {
    const token = await this.getAuthToken();
    
    if (!token) {
      throw new Error('User not authenticated');
    }

    return fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        ...options.headers,
      },
    });
  }

  /**
   * Register a new user via backend API
   */
  async register(userData: RegisterData): Promise<User> {
    try {
      const response = await fetch(`${this.baseUrl}/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(userData),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Registration failed');
      }

      const result = await response.json();
      return result.data;
    } catch (error) {
      console.error('Registration error:', error);
      throw error;
    }
  }

  /**
   * Update user role via backend API
   */
  async updateUserRoles(uid: string, roles: UserRole[], employeeId?: string): Promise<void> {
    try {
      const response = await this.authenticatedRequest(`${this.baseUrl}/users/${uid}/roles`, {
        method: 'PUT',
        body: JSON.stringify({ roles, employeeId }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to update user roles');
      }
    } catch (error) {
      console.error('Update roles error:', error);
      throw error;
    }
  }

  /**
   * Send password reset email via backend API
   */
  async resetPassword(email: string): Promise<void> {
    try {
      const response = await fetch(`${this.baseUrl}/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to send password reset');
      }
    } catch (error) {
      console.error('Password reset error:', error);
      throw error;
    }
  }

  /**
   * Update user email via backend API
   */
  async updateEmail(uid: string, email: string): Promise<void> {
    try {
      const response = await this.authenticatedRequest(`${this.baseUrl}/users/${uid}/email`, {
        method: 'PUT',
        body: JSON.stringify({ email }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to update email');
      }
    } catch (error) {
      console.error('Update email error:', error);
      throw error;
    }
  }

  /**
   * Update user password via backend API
   */
  async updatePassword(uid: string, password: string): Promise<void> {
    try {
      const response = await this.authenticatedRequest(`${this.baseUrl}/users/${uid}/password`, {
        method: 'PUT',
        body: JSON.stringify({ password }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to update password');
      }
    } catch (error) {
      console.error('Update password error:', error);
      throw error;
    }
  }

  /**
   * Update user display name via backend API
   */
  async updateDisplayName(uid: string, displayName: string): Promise<void> {
    try {
      const response = await this.authenticatedRequest(`${this.baseUrl}/users/${uid}/display-name`, {
        method: 'PUT',
        body: JSON.stringify({ displayName }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to update display name');
      }
    } catch (error) {
      console.error('Update display name error:', error);
      throw error;
    }
  }

  /**
   * Get user data via backend API
   */
  async getUserData(uid: string): Promise<User> {
    try {
      const response = await this.authenticatedRequest(`${this.baseUrl}/users/${uid}`);

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to get user data');
      }

      const result = await response.json();
      return result.data;
    } catch (error) {
      console.error('Get user data error:', error);
      throw error;
    }
  }

  /**
   * Verify current token via backend API
   */
  async verifyToken(): Promise<User> {
    try {
      const response = await this.authenticatedRequest(`${this.baseUrl}/verify`);

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Token verification failed');
      }

      const result = await response.json();
      const backendUser = result.data.user;
      
      // Map backend response to frontend User interface
      return {
        uid: backendUser.id,
        email: backendUser.email,
        displayName: `${backendUser.firstName} ${backendUser.lastName}`.trim(),
        roles: backendUser.roles || [backendUser.role] || ['admin']
      };
    } catch (error) {
      console.error('Token verification error:', error);
      throw error;
    }
  }
}

export default new AuthService();